<template>
  <div class="wallet-withdrawal-fields">
    <div class="wallet-withdrawal-fields__wrapper" v-for="field in visibleFields" :key="field.key">
      <component
        :is="fieldsTypeMap[field.key as keyof typeof fieldsTypeMap]?.component || 'form-input-text'"
        v-model:value="localFormData[field.key]"
        :type="fieldsTypeMap[field.key as keyof typeof fieldsTypeMap]?.type || 'text'"
        :label="fieldLabels[field.key as keyof typeof fieldLabels]"
        :name="field.key"
        :placeholder="tryGetFieldName(field.key, 'placeholder')"
        :options="getFieldOptions(field.key)"
        :isRequired="withdrawFormRules[field.key]?.hasOwnProperty('required')"
        :hint="fieldErrors[field.key]"
        @input="onAction(field.key)"
        @blur="onAction(field.key)"
        @focus="onFocus(field.key)"
      />

      <div v-if="field.key === 'pixKey'" class="wallet-withdrawal-fields__info">
        {{ getContent(popupsData, defaultLocalePopupsData, 'wallet.withdraw.pixInfo') }}
      </div>

      <atomic-warning v-if="field.key === 'wallet_id'">
        {{ getContent(popupsData, defaultLocalePopupsData, 'wallet.withdraw.selectNetworkWarning') }}
      </atomic-warning>
    </div>
  </div>
</template>

<script setup lang="ts">
  import fieldsTypeMap from '@skeleton/maps/fieldsTypeMap.json';
  import type { IPaymentField } from '@skeleton/core/types';
  import useVuelidate from '@vuelidate/core';

  interface WithdrawFormData {
    [key: string]: Maybe<string>;
  }

  const props = defineProps<{
    withdrawFormData: WithdrawFormData;
    serverFormErrors: WithdrawFormData;
    fields: IPaymentField[];
    selectedNetworkDataName?: string;
    methodName: string;
    activeCurrency?: string;
    networkId?: string;
    isValidateError: boolean;
    networkRegex?: any;
  }>();

  const emit = defineEmits<{
    'update:withdrawFormData': [value: WithdrawFormData];
    'update:isValidateError': [value: boolean];
    'update:serverFormErrors': [value: WithdrawFormData];
  }>();

  const { getContent } = useProjectMethods();
  const { popupsData, defaultLocalePopupsData, fieldsSettings, defaultLocaleFieldsSettings } =
    storeToRefs(useGlobalStore());

  const localFormData = reactive<{ [key: string]: Maybe<string> }>({});
  const serverLocalFormErrors = reactive<{ [key: string]: Maybe<string> }>({});

  const startRules = props.fields.reduce((currentRulesObj, currentField) => {
    if (currentField.key === 'crypto_network') return currentRulesObj;

    const rulesArr: { rule: string; arguments?: string }[] = [];
    if (currentField.isRequired) rulesArr.push({ rule: 'required' });

    if (currentField.key === 'phone') {
      rulesArr.push({ rule: 'phone' });
    } else if (currentField.regexp) {
      rulesArr.push({
        rule: 'regex',
        arguments: currentField.regexp,
      });
    } else if (currentField.key === 'wallet_id') {
      const findNetworkField = props.fields.find(field => field.key === 'crypto_network');
      const firstNetworkRegex = findNetworkField ? findNetworkField.options?.[0]?.regex : undefined;

      if (firstNetworkRegex) {
        rulesArr.push({
          rule: 'regex',
          arguments: firstNetworkRegex,
        });
      }
    }

    if (rulesArr.length) return { ...currentRulesObj, [currentField.key]: rulesArr };
    return currentRulesObj;
  }, {});

  const withdrawRules = ref(startRules);
  const { getFormRules } = useProjectMethods();
  const withdrawFormRules = computed(() => {
    return getFormRules(withdrawRules.value);
  });
  const v$ = useVuelidate(withdrawFormRules, localFormData);

  const visibleFields = computed(() => {
    return props.fields.filter(field => {
      if (['endpointId', 'crypto_network'].includes(field.key)) {
        return false;
      }

      if (!field.value && field.value !== null) {
        return false;
      }

      if (field.key === 'destination_tag') {
        return props.activeCurrency === 'TON' || ['TON'].includes(props.networkId || '');
      }

      return true;
    });
  });

  const getFieldOptions = (fieldName: string) => {
    const fieldsStore = useFieldsStore();
    return fieldsStore.selectOptions[fieldName] || [];
  };

  const tryGetFieldName = (fieldKey: string, fieldName: string) => {
    const formatedMethodName = formatPayMethodKey(props.methodName);

    return (
      getContent(
        fieldsSettings.value,
        defaultLocaleFieldsSettings.value,
        `fieldsControls.${formatedMethodName}.${fieldKey}.${fieldName}`
      ) ||
      getContent(fieldsSettings.value, defaultLocaleFieldsSettings.value, `fieldsControls.${fieldKey}.${fieldName}`)
    );
  };

  const fieldLabels = computed(() =>
    props.fields.reduce((acc, field) => {
      let label = tryGetFieldName(field.key, 'label');
      if (field.key === 'wallet_id') {
        label = label.replace('{chain}', props.selectedNetworkDataName || '');
      }
      return {
        ...acc,
        [field.key]: label,
      };
    }, {})
  );

  const onAction = (fieldKey: string) => {
    v$.value[fieldKey]?.$touch();
    emit('update:isValidateError', v$.value.$invalid);
  };

  const onFocus = (fieldName: string): void => {
    if (serverLocalFormErrors[fieldName]) {
      serverLocalFormErrors[fieldName] = undefined;
    }
  };

  const fieldErrors = computed(() => {
    return Object.fromEntries(
      props.fields.map(field => {
        const fieldKey = field.key;

        if (v$.value[fieldKey]?.$error) {
          return [
            fieldKey,
            {
              variant: 'error',
              message: v$.value[fieldKey].$errors[0]?.$message,
            },
          ];
        }

        if (serverLocalFormErrors[fieldKey]) {
          return [
            fieldKey,
            {
              variant: 'error',
              message: serverLocalFormErrors[fieldKey]?.[0] || '',
            },
          ];
        }

        return [fieldKey, undefined];
      })
    );
  });

  onMounted(() => {
    // заполняем поля по умолчанию
    props.fields.forEach(field => {
      if (field.value != null && field.key !== 'crypto_network') {
        localFormData[field.key] = field.value;
      }
    });

    emit('update:withdrawFormData', {
      ...props.withdrawFormData,
      ...localFormData,
    });

    nextTick(() => {
      props.fields.forEach(field => {
        if (field.value !== null && v$.value[field.key]?.$invalid) v$.value[field.key].$touch();
      });

      emit('update:isValidateError', v$.value.$invalid);
    });
  });

  watch(
    () => props.withdrawFormData,
    newVal => {
      Object.keys(newVal).forEach(key => {
        if (newVal[key] !== undefined && newVal[key] !== localFormData[key]) {
          localFormData[key] = newVal[key];
        }
      });
    },
    { deep: true, immediate: true }
  );

  watch(
    () => props.serverFormErrors,
    newVal => {
      Object.keys(newVal).forEach(key => {
        if (newVal[key] !== undefined && newVal[key] !== serverLocalFormErrors[key]) {
          serverLocalFormErrors[key] = newVal[key];
        }
      });
    },
    { deep: true, immediate: true }
  );

  watch(
    localFormData,
    newVal => {
      emit('update:withdrawFormData', {
        ...props.withdrawFormData,
        ...newVal,
      });
    },
    { deep: true }
  );

  watch(
    serverLocalFormErrors,
    newVal => {
      emit('update:serverFormErrors', {
        ...props.serverFormErrors,
        ...newVal,
      });
    },
    { deep: true }
  );

  watch(
    () => props.networkId,
    () => {
      v$.value.$reset();

      // сброс поля адреса кошелька при смене сети
      localFormData['wallet_id'] = '';

      // новые правила валидации для адреса кошелька
      const findNetworkField = props.fields.find(field => field.key === 'crypto_network');

      if (!findNetworkField) return;

      const findNetworkOption = findNetworkField?.options?.find(option => {
        if (option.id) return option.id === props.networkId;
        return props.networkId === 'native';
      });

      const regex = findNetworkOption?.regex;

      withdrawRules.value = {
        ...withdrawRules.value,
        wallet_id: regex
          ? [
              { rule: 'required' },
              {
                rule: 'regex',
                arguments: regex,
              },
            ]
          : [{ rule: 'required' }],
      };
    }
  );
</script>

<style src="~/assets/styles/components/wallet/withdrawal-fields.scss" lang="scss"></style>
